import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import PageShell from '../ui/PageShell';
import {
  getPassportFlags,
  isPassportEnabled,
  listPassportCollections,
  type PassportCollectionDefinition,
  type PassportFeatureFlags,
} from '../../services/api/passport';
import { PASSPORT_DISABLED_DESCRIPTION, PASSPORT_DISABLED_TITLE } from './passportCopy';

const PassportCollectionsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [collections, setCollections] = useState<PassportCollectionDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/passport/collections' }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await getPassportFlags();
      if (!cancelled) {
        setFlags(f);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPassportCollections();
      setCollections(data);
      void apiService.trackEvent('passport_collection_viewed', { metadata: { view: 'list' } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || flagsLoading) return;
    if (!isPassportEnabled(flags) || !flags?.passport_collections_enabled) return;
    void load();
  }, [authLoading, isAuthenticated, flagsLoading, flags, load]);

  if (authLoading || !isAuthenticated || flagsLoading) {
    return <PageLoader message="Loading…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isPassportEnabled(flags) || !flags?.passport_collections_enabled) {
    return (
      <PageShell contentClassName="!py-6 sm:!py-8">
        <PageHeader eyebrow="Bilt Passport" title="Collections" backLink={{ label: 'Back to Passport', to: '/passport' }} />
        <EmptyState title={PASSPORT_DISABLED_TITLE} description={PASSPORT_DISABLED_DESCRIPTION} />
      </PageShell>
    );
  }

  return (
    <PageShell contentClassName="!py-6 sm:!py-8">
      <PageHeader
        eyebrow="Bilt Passport"
        title="Collections"
        description="Structured sets built from your verified encounters — manufacturer, chassis, event, and more."
        backLink={{ label: 'Back to Passport', to: '/passport' }}
      />

      {loading ? (
        <p className="text-gray-400 text-sm" role="status">
          Loading collections…
        </p>
      ) : error ? (
        <EmptyState
          title="Couldn't load collections"
          description={error}
          action={
            <Button type="button" variant="primary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      ) : collections.length === 0 ? (
        <EmptyState title="No collections yet" description="Check back as more collections are published." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map((c) => (
            <div key={c.public_id} className="card p-5">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{c.collection_type}</p>
              <h3 className="text-base font-semibold text-white mb-1">{c.title}</h3>
              {c.subtitle ? <p className="text-sm text-gray-400 mb-3">{c.subtitle}</p> : null}
              {c.progress ? (
                <div className="mt-3">
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full bg-orange-500 rounded-full"
                      style={{ width: `${Math.min(100, Math.max(0, c.progress.percent_complete))}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">{Math.round(c.progress.percent_complete)}% complete</p>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
};

export default PassportCollectionsPage;
